// ============================================
// useInventoryAlerts - shared source for the global Layout bell and any
// per-page bell that wants to surface inventory alerts (low stock + expiry).
// ============================================
//
// Severity-bracket IDs:
//   low-${invId}-low      (3 doses or fewer)
//   low-${invId}-last     (1 dose remaining)
//   low-${invId}-empty    (0 doses)
//   exp-${invId}-soon     (7 days or less)
//   exp-${invId}-urgent   (3 days or less)
//   exp-${invId}-expired  (0 days or past)
//
// IDs encode the bracket so worsening conditions create new alerts that
// re-surface even after the user dismissed the prior bracket. Don't change
// these IDs without considering the dismissedAlertIds in production
// localStorage - old bracket IDs become orphaned (harmless).

import { useEffect, useMemo, useState } from 'react';
import { useCloudQuery } from '../../data/hooks';
import { fetchInventory } from '../../data/cloud';

export type AlertSeverity = 'info' | 'warn' | 'critical';
export type AlertType = 'low' | 'expiring';

export interface InventoryAlert {
  id: string;
  type: AlertType;
  label: string;
  detail: string;
  severity: AlertSeverity;
}

export interface InventoryAlertsResult {
  alerts: InventoryAlert[];          // raw, unfiltered, computed from current inventory
  unseenAlerts: InventoryAlert[];    // alerts whose id is NOT in seenAlertIds
  visibleAlerts: InventoryAlert[];   // alerts whose id is NOT in dismissedAlertIds
  alertCount: number;                // unseenAlerts.length (used by the badge)
  markAllSeen: () => void;
  dismiss: (id: string) => void;
}

const SEEN_KEY = 'prtcl_seen_alerts';
const DISMISSED_KEY = 'prtcl_dismissed_alerts';

function loadIdSet(key: string): Set<string> {
  try {
    const stored = localStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

function persistIdSet(key: string, ids: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // localStorage unavailable - non-fatal
  }
}

// Pure derivation: kept top-level + exported so tests can exercise the
// severity-bracket logic without rendering the hook.
export function deriveAlerts(
  inventory: ReadonlyArray<{
    id: string;
    isActive: boolean;
    peptideName: string;
    // Nullable since 2026-04-29: vials added without a planned dose
    // start with null counts. Such vials skip low-stock alerts entirely.
    dosesRemaining: number | null;
    expirationDate: string;
    isResearching?: boolean;
  }>,
  now: Date = new Date(),
): InventoryAlert[] {
  const items: InventoryAlert[] = [];
  for (const inv of inventory) {
    if (!inv.isActive) continue;
    // Researching vials and dose-less vials don't have stock data to
    // alert on. Skip them — they re-enter the alert path once started
    // and once a first dose is logged (RPC backfills total/remaining).
    if (inv.isResearching) continue;
    if (inv.dosesRemaining == null) continue;

    // Low-stock brackets
    if (inv.dosesRemaining <= 0) {
      items.push({
        id: `low-${inv.id}-empty`,
        type: 'low',
        severity: 'critical',
        label: inv.peptideName,
        detail: 'Vial empty - replace to continue dosing',
      });
    } else if (inv.dosesRemaining <= 1) {
      items.push({
        id: `low-${inv.id}-last`,
        type: 'low',
        severity: 'warn',
        label: inv.peptideName,
        detail: '1 dose remaining',
      });
    } else if (inv.dosesRemaining <= 3) {
      items.push({
        id: `low-${inv.id}-low`,
        type: 'low',
        severity: 'info',
        label: inv.peptideName,
        detail: `${inv.dosesRemaining} doses remaining`,
      });
    }

    // Expiration brackets
    const expDate = new Date(inv.expirationDate);
    const daysLeft = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) {
      items.push({
        id: `exp-${inv.id}-expired`,
        type: 'expiring',
        severity: 'critical',
        label: inv.peptideName,
        detail: 'Expired - do not use',
      });
    } else if (daysLeft <= 3) {
      items.push({
        id: `exp-${inv.id}-urgent`,
        type: 'expiring',
        severity: 'warn',
        label: inv.peptideName,
        detail: `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`,
      });
    } else if (daysLeft <= 7) {
      items.push({
        id: `exp-${inv.id}-soon`,
        type: 'expiring',
        severity: 'info',
        label: inv.peptideName,
        detail: `Expires in ${daysLeft} days`,
      });
    }
  }
  return items;
}

export function useInventoryAlerts(): InventoryAlertsResult {
  const { data: inventory } = useCloudQuery('inventory', fetchInventory, []);
  const [seenAlertIds, setSeenAlertIds] = useState<Set<string>>(() => loadIdSet(SEEN_KEY));
  const [dismissedAlertIds, setDismissedAlertIds] = useState<Set<string>>(() => loadIdSet(DISMISSED_KEY));

  const alerts = useMemo<InventoryAlert[]>(() => deriveAlerts(inventory), [inventory]);

  const unseenAlerts = useMemo(
    () => alerts.filter((a) => !seenAlertIds.has(a.id)),
    [alerts, seenAlertIds],
  );
  const visibleAlerts = useMemo(
    () => alerts.filter((a) => !dismissedAlertIds.has(a.id)),
    [alerts, dismissedAlertIds],
  );

  // Persist set changes to localStorage on write paths.
  useEffect(() => { persistIdSet(SEEN_KEY, seenAlertIds); }, [seenAlertIds]);
  useEffect(() => { persistIdSet(DISMISSED_KEY, dismissedAlertIds); }, [dismissedAlertIds]);

  function markAllSeen() {
    setSeenAlertIds(new Set(alerts.map((a) => a.id)));
  }

  function dismiss(id: string) {
    setDismissedAlertIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  return {
    alerts,
    unseenAlerts,
    visibleAlerts,
    alertCount: unseenAlerts.length,
    markAllSeen,
    dismiss,
  };
}
